import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  ChevronLeft,
  FileImage,
  Upload,
  Layers,
  MoreHorizontal,
  ExternalLink,
  User as UserIcon,
  Printer,
  Pencil,
  Archive,
  ArchiveRestore,
  Trash2,
} from "lucide-react";
import { useDemoMode } from "@/hooks/use-demo-mode";
import { useAuth } from "@/hooks/use-auth";
import { useGangSheetBuilderScript } from "@/hooks/useGangSheetBuilderScript";
import { getDataSource } from "@/lib/data-source";
import { JOB_STATUSES, type JobStatus } from "@/lib/types";
import { StatusChip } from "@/components/StatusChip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { listCuttingTiers, listShippingMethods } from "@/lib/products";
import { supabase } from "@/integrations/supabase/client";
import { createNotification, createOrUpdateNotification } from "@/lib/notifications";
import { useDraftEdit } from "@/hooks/use-draft-edit";
import { DraftPublishBar } from "@/components/DraftPublishBar";
import { FavouriteButton } from "@/components/FavouriteButton";

export const Route = createFileRoute("/app/staff/jobs/$id")({ component: StaffJobDetail });

function StaffJobDetail() {
  const { id } = Route.useParams();
  const { demoMode } = useDemoMode();
  const { profile } = useAuth();
  const ds = useMemo(
    () => getDataSource(profile?.role === "staff" && demoMode),
    [demoMode, profile?.role],
  );
  const qc = useQueryClient();

  const { data: job } = useQuery({ queryKey: ["job", demoMode, id], queryFn: () => ds.getJob(id) });
  const { data: files = [] } = useQuery({
    queryKey: ["files", demoMode, id],
    queryFn: () => ds.listFiles(id),
  });
  const { data: activity = [] } = useQuery({
    queryKey: ["activity", demoMode, id],
    queryFn: () => ds.listActivity(id),
  });
  const { data: cuttingTiers = [] } = useQuery({
    queryKey: ["cutting-tiers"],
    queryFn: listCuttingTiers,
  });
  const { data: shippingMethods = [] } = useQuery({
    queryKey: ["shipping-methods"],
    queryFn: listShippingMethods,
  });
  const { data: staffProfiles = [] } = useQuery({
    queryKey: ["staff-profiles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .eq("role", "staff");
      if (error) throw error;
      return data ?? [];
    },
  });

  const [activeTab, setActiveTab] = useState("summary");

  type JobDraft = {
    status: JobStatus;
    payment_status: "pending" | "confirmed" | "invoiced";
    assignee_profile_id: string | null;
    notes_append: string;
  };
  const initialDraft = useMemo<JobDraft>(
    () => ({
      status: (job?.status ?? "New") as JobStatus,
      payment_status: (job?.payment_status ?? "pending") as JobDraft["payment_status"],
      assignee_profile_id: job?.assignee_profile_id ?? null,
      notes_append: "",
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [job?.id, job?.status, job?.payment_status, job?.assignee_profile_id],
  );

  const formatAssignee = (v: string | null) => {
    if (!v) return "Unassigned";
    const p = staffProfiles.find((x) => x.id === v);
    return p?.full_name ?? p?.email ?? v;
  };

  const draftEdit = useDraftEdit<JobDraft>({
    initial: initialDraft,
    labels: {
      status: "Status",
      payment_status: "Payment status",
      assignee_profile_id: "Assignee",
      notes_append: "Add note",
    },
    format: {
      assignee_profile_id: (v: string | null) => formatAssignee(v),
      notes_append: (v: string) => (v ? `+ "${v.slice(0, 60)}${v.length > 60 ? "…" : ""}"` : "—"),
    },
    validate: (d) => {
      const e: Partial<Record<keyof JobDraft, string>> = {};
      if (d.status === "Completed" && job?.quality_status && job.quality_status !== "ok") {
        e.status = `Quality status is "${job.quality_status}". Resolve before completing.`;
      }
      if (d.payment_status === "invoiced" && !d.assignee_profile_id) {
        e.assignee_profile_id = "Assign a staff member before invoicing.";
      }
      if (d.notes_append.length > 5000) {
        e.notes_append = "Note too long (max 5000 characters).";
      }
      return e;
    },
    onPublish: async (changes) => {
      try {
        const patch: Record<string, unknown> = {};
        if ("status" in changes) patch.status = changes.status;
        if ("payment_status" in changes) patch.payment_status = changes.payment_status;
        if ("assignee_profile_id" in changes)
          patch.assignee_profile_id = changes.assignee_profile_id;
        if ("notes_append" in changes && changes.notes_append) {
          patch.notes = (job?.notes ? job.notes + "\n" : "") + changes.notes_append;
        }
        await ds.updateJob(id, patch as never);
        qc.invalidateQueries({ queryKey: ["job", demoMode, id] });
        qc.invalidateQueries({ queryKey: ["staff-jobs", demoMode] });
        qc.invalidateQueries({ queryKey: ["activity", demoMode, id] });
        toast.success("Changes published");
        // Notify customer when status changes
        if ("status" in changes && job?.customer_id) {
          try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { data: customerProfiles } = await (supabase as any)
              .from("profiles")
              .select("id")
              .eq("customer_id", job.customer_id)
              .eq("role", "customer");
            const recipients: string[] = (customerProfiles ?? [])
              .map((p: { id: string }) => p.id)
              .filter((pid: string) => pid && pid !== profile?.id);
            const isOnHold = changes.status === "On Hold";
            await Promise.all(
              recipients.map((uid) =>
                createNotification({
                  user_id: uid,
                  type: isOnHold ? "job_on_hold" : "job_status_change",
                  title: isOnHold ? "Your job is on hold" : `Job status: ${changes.status}`,
                  message: `"${job.job_title}" is now ${changes.status}.`,
                  data: { job_id: id },
                  priority: isOnHold ? 2 : 1,
                }).catch(() => null),
              ),
            );
          } catch (notifyErr) {
            console.error("Notify customer of status change failed:", notifyErr);
          }
        }
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Publish failed");
        throw e;
      }
    },
  });

  useEffect(() => {
    draftEdit.reset(initialDraft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDraft]);

  const handleAssignToMe = () => {
    if (!profile?.id) return;
    draftEdit.set("assignee_profile_id", profile.id);
  };

  const {
    isLoaded: builderLoaded,
    isLoading: builderLoading,
    error: builderError,
    isEnabled: builderEnabled,
    shopId: dripShopId,
    openBuilder,
  } = useGangSheetBuilderScript();

  const [reopeningGangSheet, setReopeningGangSheet] = useState(false);
  const [pendingReopenDesignId, setPendingReopenDesignId] = useState<string | null>(null);
  const [versioningGangSheet, setVersioningGangSheet] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [menuBusy, setMenuBusy] = useState(false);

  const navigate = useNavigate();

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: async (accepted) => {
      for (const f of accepted) {
        try {
          await ds.addFile(id, f);
        } catch (e) {
          toast.error(e instanceof Error ? e.message : "Upload failed");
        }
      }
      qc.invalidateQueries({ queryKey: ["files", demoMode, id] });
      toast.success("Uploaded");
    },
    accept: { "image/*": [], "application/pdf": [] },
  });

  const openRename = () => {
    if (!job) return;
    setRenameValue(job.job_title ?? "");
    setRenameOpen(true);
  };

  const doRename = async () => {
    const next = renameValue.trim();
    if (!next || next === job?.job_title) {
      setRenameOpen(false);
      return;
    }
    setMenuBusy(true);
    try {
      await ds.updateJob(id, { job_title: next } as never);
      qc.invalidateQueries({ queryKey: ["job", demoMode, id] });
      qc.invalidateQueries({ queryKey: ["staff-jobs", demoMode] });
      toast.success("Job renamed");
      setRenameOpen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Rename failed");
    } finally {
      setMenuBusy(false);
    }
  };

  const doArchiveToggle = async () => {
    if (!job) return;
    const isArchived = !!job.archived_at;
    setMenuBusy(true);
    try {
      if (isArchived) {
        await ds.unarchiveJob?.(id);
        toast.success("Job restored");
      } else {
        await ds.archiveJob?.(id);
        toast.success("Job archived");
      }
      qc.invalidateQueries({ queryKey: ["job", demoMode, id] });
      qc.invalidateQueries({ queryKey: ["staff-jobs", demoMode] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Archive failed");
    } finally {
      setMenuBusy(false);
    }
  };

  const doDelete = async () => {
    setMenuBusy(true);
    try {
      await ds.deleteJob?.(id);
      toast.success("Job deleted");
      setConfirmDeleteOpen(false);
      qc.invalidateQueries({ queryKey: ["staff-jobs", demoMode] });
      navigate({ to: "/app/staff/jobs-board" });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Delete failed");
    } finally {
      setMenuBusy(false);
    }
  };

  if (!job) return <div className="text-muted-foreground">Loading…</div>;

  const setAsGangSheet = async (fileId: string) => {
    try {
      await ds.updateFileType(fileId, "gang_sheet");
      qc.invalidateQueries({ queryKey: ["files", demoMode, id] });
      toast.success("Marked as gang sheet");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update file");
    }
  };

  const requestReopenGangSheet = (designId: string) => {
    if (job?.gangsheet_preview_url || job?.gangsheet_saved_at) {
      setPendingReopenDesignId(designId);
      return;
    }
    void openGangSheetBuilder(designId);
  };

  const snapshotCurrentGangSheet = async (): Promise<boolean> => {
    if (!job?.gangsheet_preview_url) return true;
    try {
      const versionLabel = new Date().toISOString().replace(/[:.]/g, "-");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any).from("job_files").insert({
        job_id: id,
        file_url: job.gangsheet_preview_url,
        file_name:
          `Gang sheet ${job.gangsheet_design_id ?? ""} (snapshot ${versionLabel}).png`.trim(),
        file_type: "gang_sheet_version",
      });
      if (error) throw error;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (supabase as any).from("job_activity").insert({
        job_id: id,
        event_type: "gang_sheet_versioned",
        message: `Snapshot of gang sheet ${
          job.gangsheet_design_id ?? "—"
        } saved before reopening builder${
          job.gangsheet_saved_at
            ? ` (was last saved ${new Date(job.gangsheet_saved_at).toLocaleString()})`
            : ""
        }`,
      });
      qc.invalidateQueries({ queryKey: ["files", demoMode, id] });
      qc.invalidateQueries({ queryKey: ["activity", demoMode, id] });
      return true;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not snapshot existing gang sheet");
      return false;
    }
  };

  const confirmReopenWithVersion = async () => {
    const designId = pendingReopenDesignId;
    if (!designId) return;
    setVersioningGangSheet(true);
    const ok = await snapshotCurrentGangSheet();
    setVersioningGangSheet(false);
    if (!ok) return;
    setPendingReopenDesignId(null);
    toast.success("Saved current gang sheet as a version");
    void openGangSheetBuilder(designId);
  };

  const confirmReopenOverwrite = () => {
    const designId = pendingReopenDesignId;
    setPendingReopenDesignId(null);
    if (designId) void openGangSheetBuilder(designId);
  };

  const openGangSheetBuilder = async (designId?: string) => {
    if (!builderEnabled) {
      toast.error("Gang sheet builder is not enabled.");
      return;
    }
    if (!builderLoaded) {
      toast.error(builderError ?? "Builder is still loading, please try again");
      return;
    }
    if (!dripShopId) {
      toast.error("Drip shop ID not configured. Please contact support.");
      return;
    }

    setReopeningGangSheet(true);
    try {
      await openBuilder({
        design_id: designId,
        onSaved: async (newDesignId, info) => {
          try {
            const designName = info?.designName?.trim() || null;
            await ds.updateJob(id, {
              gangsheet_design_id: newDesignId,
              gangsheet_preview_url: info?.previewUrl ?? null,
              gangsheet_saved_at: info?.savedAt ?? new Date().toISOString(),
              // Job name follows the design name the user typed in the builder
              ...(designName ? { job_title: designName, gang_sheet_name: designName } : {}),
            });

            // Attach the saved gang sheet as a downloadable job file so staff
            // can grab the latest PNG without leaving the job page.
            if (info?.previewUrl) {
              try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                await (supabase as any).from("job_files").insert({
                  job_id: id,
                  file_url: info.previewUrl,
                  file_name: `${designName ?? `Gang sheet ${newDesignId}`}.png`,
                  file_type: "gang_sheet",
                });
                qc.invalidateQueries({ queryKey: ["files", demoMode, id] });
              } catch (fileErr) {
                console.warn("Failed to attach gang sheet file", fileErr);
              }
            }

            qc.invalidateQueries({ queryKey: ["job", demoMode, id] });
            qc.invalidateQueries({ queryKey: ["activity", demoMode, id] });
            toast.success("Gang sheet updated");
            // Notify the customer that staff updated their gang sheet
            if (profile?.role === "staff" || profile?.role === "admin") {
              try {
                if (job?.customer_id) {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const { data: recipients } = await (supabase as any)
                    .from("profiles")
                    .select("id")
                    .eq("customer_id", job.customer_id)
                    .eq("role", "customer");
                  await Promise.all(
                    (recipients ?? [])
                      .filter((r: { id: string }) => r.id && r.id !== profile?.id)
                      .map((r: { id: string }) =>
                        createOrUpdateNotification(
                          {
                            user_id: r.id,
                            type: "gang_sheet_updated",
                            title: "Gang sheet updated",
                            message: `Your team updated the gang sheet for "${designName ?? job?.job_title ?? "your job"}".`,
                            data: {
                              job_id: id,
                              design_id: newDesignId,
                              preview_url: info?.previewUrl ?? null,
                            },
                            priority: 1,
                          },
                          { dedupeKey: "job_id", dedupeValue: id, windowMinutes: 10 },
                        ),
                      ),
                  );
                }
              } catch (notifyErr) {
                // Non-fatal: surface in console only
                console.warn("Failed to notify customer of gang sheet update", notifyErr);
              }
            }
          } catch (e) {
            toast.error(e instanceof Error ? e.message : "Failed to save gang sheet update");
          }
        },
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to open builder");
    } finally {
      setReopeningGangSheet(false);
    }
  };

  return (
    <div className="space-y-6">
      <Link
        to="/app/staff/jobs-board"
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> Back to board
      </Link>

      {/* Gradient hero — matches customer job detail */}
      <div className="relative overflow-hidden rounded-[2rem] bg-gradient-hero p-8 text-primary-foreground shadow-pop md:p-10">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-[oklch(0.88_0.16_95/0.35)] blur-2xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-24 -left-12 h-56 w-56 rounded-full bg-[oklch(0.7_0.22_350/0.35)] blur-2xl"
        />
        <div className="relative flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary-foreground/80">
              <span>
                {job.order_number ? `Order #${job.order_number}` : `Job ${job.id.slice(0, 8)}`}
              </span>
              {job.archived_at && (
                <span className="rounded-full bg-background/20 px-2 py-0.5 text-[10px]">
                  Archived
                </span>
              )}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight md:text-4xl lg:text-5xl truncate">
                {job.gang_sheet_name || job.job_title}
              </h1>
              <FavouriteButton jobId={job.id} size="sm" />
            </div>
            <p className="mt-2 text-base text-primary-foreground/90">
              {job.product_type ?? "—"}
              {job.quantity ? ` · Qty ${job.quantity}` : ""}
              {job.metres ? ` · ${job.metres}m` : ""}
            </p>
            {job.gang_sheet_name && (
              <p className="mt-1 text-sm text-primary-foreground/80">
                <span className="font-semibold">Design:</span> {job.gang_sheet_name}
              </p>
            )}
            {job.customer && (
              <p className="mt-1 text-sm text-primary-foreground/80">
                <span className="font-semibold">Customer:</span>{" "}
                {job.customer.company_name ||
                  job.customer.contact_name ||
                  job.customer.email ||
                  "—"}
              </p>
            )}
          </div>
          <div className="flex flex-col items-start gap-3 md:items-end">
            <div className="flex items-center gap-2">
              <StatusChip
                status={job.status}
                className="rounded-full bg-card px-4 py-2 text-sm font-semibold shadow-soft"
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="rounded-full bg-card/90 text-foreground hover:bg-card"
                    aria-label="Job actions"
                  >
                    <MoreHorizontal className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onSelect={openRename}>
                    <Pencil className="h-4 w-4 mr-2" /> Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={doArchiveToggle} disabled={menuBusy}>
                    {job.archived_at ? (
                      <>
                        <ArchiveRestore className="h-4 w-4 mr-2" /> Restore
                      </>
                    ) : (
                      <>
                        <Archive className="h-4 w-4 mr-2" /> Archive
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => setConfirmDeleteOpen(true)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="text-right text-sm text-primary-foreground/85">
              <div className="text-[11px] uppercase tracking-wide text-primary-foreground/70">
                Due
              </div>
              <div className="font-medium">
                {job.due_date ? format(new Date(job.due_date), "d MMM yyyy") : "Not set"}
                {job.due_date && new Date(job.due_date) < new Date() && (
                  <span className="ml-1 text-xs text-amber-200">(Overdue)</span>
                )}
              </div>
            </div>
            <div className="text-right text-sm text-primary-foreground/85">
              <div className="text-[11px] uppercase tracking-wide text-primary-foreground/70">
                Assignee
              </div>
              <div className="font-medium">
                {job.assignee_profile_id
                  ? (staffProfiles.find((p) => p.id === job.assignee_profile_id)?.full_name ??
                    staffProfiles.find((p) => p.id === job.assignee_profile_id)?.email ??
                    "—")
                  : "Unassigned"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rename dialog */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename job</DialogTitle>
            <DialogDescription>
              Update the job title shown to staff and the customer.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            placeholder="Job title"
            autoFocus
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenameOpen(false)} disabled={menuBusy}>
              Cancel
            </Button>
            <Button onClick={doRename} disabled={menuBusy || !renameValue.trim()}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this job?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the job and its files. This action cannot be undone. If you
              just want to hide it, archive instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={menuBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              disabled={menuBusy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Tabs for main content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="rounded-2xl bg-secondary/50 p-1">
          <TabsTrigger value="summary" className="rounded-xl">
            Summary
          </TabsTrigger>
          <TabsTrigger value="artwork" className="rounded-xl">
            Artwork & Files
          </TabsTrigger>
          <TabsTrigger value="activity" className="rounded-xl">
            Activity & Notes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="space-y-5 lg:col-span-2">
              {/* Job Summary Card */}
              <div className="rounded-3xl border border-border bg-card p-5 shadow-soft">
                <h3 className="mb-4 font-semibold text-sm uppercase tracking-wide text-muted-foreground">
                  Job Summary
                </h3>
                <div className="grid gap-3 sm:grid-cols-2 text-sm">
                  <div>
                    <div className="text-xs text-muted-foreground">Product</div>
                    <div className="font-medium">{job.product_type ?? "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Print size</div>
                    <div className="font-medium">{job.print_size ?? "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Quantity</div>
                    <div className="font-medium">{String(job.quantity ?? 0)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Metres</div>
                    <div className="font-medium">{job.metres ?? "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Colours</div>
                    <div className="font-medium">{job.colours ?? "—"}</div>
                  </div>
                  {job.cutting_tier_id && (
                    <div>
                      <div className="text-xs text-muted-foreground">Cutting</div>
                      <div className="font-medium">
                        {cuttingTiers.find((t) => t.id === job.cutting_tier_id)?.name || "—"}
                      </div>
                    </div>
                  )}
                  <div>
                    <div className="text-xs text-muted-foreground">Shipping method</div>
                    <div className="font-medium">
                      {shippingMethods.find((m) => m.id === job.shipping_method_id)?.name ??
                        "Standard"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Payment status</div>
                    <div className="font-medium capitalize">{job.payment_status ?? "pending"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">VAT</div>
                    <div className="font-medium">
                      {job.vat_amount ? `£${Number(job.vat_amount).toFixed(2)}` : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Shipping cost</div>
                    <div className="font-medium">
                      {job.shipping_cost ? `£${Number(job.shipping_cost).toFixed(2)}` : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Total</div>
                    <div className="font-medium">
                      {job.total_cost ? `£${Number(job.total_cost).toFixed(2)}` : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Gang sheet</div>
                    <div
                      className={`font-medium ${job.gangsheet_design_id ? "text-green-600 dark:text-green-400" : "text-amber-600 dark:text-amber-400"}`}
                    >
                      {job.gangsheet_design_id ? "Built" : "Missing"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Details Card */}
              <div className="rounded-3xl border border-border bg-card p-5 shadow-soft">
                <h3 className="mb-4 font-semibold text-sm uppercase tracking-wide text-muted-foreground">
                  Order Details
                </h3>
                <div className="space-y-3 text-sm">
                  {job.shipping_address && (
                    <div>
                      <div className="text-xs text-muted-foreground">Shipping address</div>
                      <div className="font-medium whitespace-pre-wrap">{job.shipping_address}</div>
                    </div>
                  )}
                  {job.billing_address && (
                    <div>
                      <div className="text-xs text-muted-foreground">Billing address</div>
                      <div className="font-medium whitespace-pre-wrap">{job.billing_address}</div>
                    </div>
                  )}
                  {!job.shipping_address && !job.billing_address && (
                    <div className="text-xs text-muted-foreground">No address details provided</div>
                  )}
                </div>
              </div>

              {/* Customer block */}
              {job.customer && (
                <div className="rounded-3xl border border-border bg-card p-5 shadow-soft">
                  <h3 className="mb-4 font-semibold text-sm uppercase tracking-wide text-muted-foreground">
                    Customer
                  </h3>
                  <div className="text-sm">
                    <div className="font-medium">
                      {job.customer.company_name || job.customer.contact_name || "—"}
                    </div>
                    <div className="text-muted-foreground">{job.customer.email || "—"}</div>
                    {job.customer_profile_id && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3 rounded-full"
                        onClick={() =>
                          navigate({
                            to: "/app/staff/users",
                            search: { id: job.customer_profile_id },
                          })
                        }
                      >
                        <UserIcon className="h-3 w-3 mr-1" /> View customer in FlightDeck
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Status & Assignee controls */}
              <div className="rounded-3xl border border-border bg-card p-5 shadow-soft">
                <h3 className="mb-4 font-semibold text-sm uppercase tracking-wide text-muted-foreground">
                  Job Controls
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label>Assignee</Label>
                    <div className="flex gap-2 mt-1.5">
                      <Select
                        value={draftEdit.draft.assignee_profile_id ?? "unassigned"}
                        onValueChange={(value) =>
                          draftEdit.set(
                            "assignee_profile_id",
                            value === "unassigned" ? null : value,
                          )
                        }
                      >
                        <SelectTrigger className="rounded-2xl flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {staffProfiles.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.full_name ?? p.email}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {profile?.role === "staff" && (
                        <Button
                          onClick={handleAssignToMe}
                          disabled={draftEdit.draft.assignee_profile_id === profile.id}
                          variant="outline"
                          className="rounded-full"
                        >
                          Assign to me
                        </Button>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <div className="mt-1.5">
                      <Select
                        value={draftEdit.draft.status}
                        onValueChange={(v) => draftEdit.set("status", v as JobStatus)}
                      >
                        <SelectTrigger className="rounded-2xl">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {JOB_STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Payment status</Label>
                    <div className="mt-1.5">
                      <Select
                        value={draftEdit.draft.payment_status}
                        onValueChange={(v) =>
                          draftEdit.set("payment_status", v as "pending" | "confirmed" | "invoiced")
                        }
                      >
                        <SelectTrigger className="rounded-2xl">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="invoiced">Invoiced</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {job.quality_status && (
                    <div>
                      <Label>Quality status</Label>
                      <div className="mt-1.5">
                        <span
                          className={
                            job.quality_status === "ok"
                              ? "inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-green-100 text-green-800"
                              : job.quality_status === "warned"
                                ? "inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-amber-100 text-amber-800"
                                : "inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-red-100 text-red-800"
                          }
                        >
                          {job.quality_status.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {job.gangsheet_design_id && !job.gangsheet_confirmed_at && (
                <div className="rounded-3xl bg-amber-50 dark:bg-amber-950/20 border-2 border-amber-200 dark:border-amber-800 p-5">
                  <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-200">
                    ⚠️ Gang sheet requires approval
                  </h3>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
                    Review and approve the gang sheet to attach it to the job.
                  </p>
                  <div className="flex items-start gap-3 mb-3">
                    <a
                      href={`https://app.buildagangsheet.com/designs/${job.gangsheet_design_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="h-32 w-32 shrink-0 overflow-hidden rounded-xl border border-border bg-background hover:ring-2 hover:ring-primary transition"
                      title="Open saved layout in a new tab"
                    >
                      {job.gangsheet_preview_url ? (
                        <img
                          src={job.gangsheet_preview_url}
                          alt="Gang sheet preview"
                          className="h-full w-full object-contain"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="grid h-full w-full place-items-center text-muted-foreground text-xs">
                          No preview
                        </div>
                      )}
                    </a>
                    <div className="text-sm space-y-1">
                      <div className="font-medium">
                        Design ID: <span className="font-mono">{job.gangsheet_design_id}</span>
                      </div>
                      {job.gangsheet_saved_at && (
                        <div className="text-xs text-muted-foreground">
                          Saved{" "}
                          {new Date(job.gangsheet_saved_at).toLocaleString(undefined, {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })}
                        </div>
                      )}
                      <a
                        href={`https://app.buildagangsheet.com/designs/${job.gangsheet_design_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" /> View saved layout
                      </a>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => requestReopenGangSheet(job.gangsheet_design_id!)}
                      disabled={builderLoading || reopeningGangSheet || !builderEnabled}
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      {reopeningGangSheet ? "Opening…" : "Edit gang sheet"}
                    </Button>
                    <Button
                      onClick={async () => {
                        try {
                          await ds.updateJob(id, {
                            gangsheet_confirmed_at: new Date().toISOString(),
                            gangsheet_confirmed_by: profile?.id,
                          });
                          qc.invalidateQueries({ queryKey: ["job", demoMode, id] });
                          toast.success("Gang sheet approved");
                        } catch (e) {
                          toast.error(e instanceof Error ? e.message : "Failed to approve gang sheet");
                        }
                      }}
                      size="sm"
                      className="rounded-full bg-green-600 hover:bg-green-700 text-white"
                    >
                      Approve & attach to job
                    </Button>
                  </div>
                </div>
              )}

              {job.gangsheet_design_id && job.gangsheet_confirmed_at && (
                <div className="rounded-3xl bg-gradient-soft p-5">
                  <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    Gang sheet design
                  </h3>
                  <div className="flex items-start gap-3 mb-3">
                    <a
                      href={`https://app.buildagangsheet.com/designs/${job.gangsheet_design_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="h-32 w-32 shrink-0 overflow-hidden rounded-xl border border-border bg-background hover:ring-2 hover:ring-primary transition"
                      title="Open saved layout in a new tab"
                    >
                      {job.gangsheet_preview_url ? (
                        <img
                          src={job.gangsheet_preview_url}
                          alt="Gang sheet preview"
                          className="h-full w-full object-contain"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="grid h-full w-full place-items-center text-muted-foreground text-xs">
                          No preview
                        </div>
                      )}
                    </a>
                    <div className="text-sm space-y-1">
                      <div className="font-medium">
                        Design ID: <span className="font-mono">{job.gangsheet_design_id}</span>
                      </div>
                      {job.gangsheet_saved_at && (
                        <div className="text-xs text-muted-foreground">
                          Saved{" "}
                          {new Date(job.gangsheet_saved_at).toLocaleString(undefined, {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })}
                        </div>
                      )}
                      {job.gangsheet_confirmed_at && (
                        <div className="text-xs text-green-600 dark:text-green-400 font-medium">
                          Approved{" "}
                          {new Date(job.gangsheet_confirmed_at).toLocaleString(undefined, {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })}
                        </div>
                      )}
                      <a
                        href={`https://app.buildagangsheet.com/designs/${job.gangsheet_design_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" /> View saved layout
                      </a>
                    </div>
                  </div>
                  <Button
                    onClick={() => requestReopenGangSheet(job.gangsheet_design_id!)}
                    disabled={builderLoading || reopeningGangSheet || !builderEnabled}
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    {reopeningGangSheet ? "Opening…" : "Reopen & edit gang sheet"}
                  </Button>
                </div>
              )}

              {!job.gangsheet_design_id && (
                <div className="rounded-3xl bg-amber-50 dark:bg-amber-950/20 border-2 border-amber-200 dark:border-amber-800 p-5">
                  <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-200">
                    ⚠️ Gang sheet required
                  </h3>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
                    This job needs a gang sheet design before it can proceed to production.
                  </p>
                  <Button
                    onClick={() => openGangSheetBuilder()}
                    disabled={builderLoading || !builderEnabled}
                    size="sm"
                    className="rounded-full"
                  >
                    {builderLoading ? "Opening builder…" : "Create gang sheet"}
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-5">
              {job.notes && (
                <div className="rounded-3xl border border-border bg-card p-5 shadow-soft">
                  <h3 className="mb-3 font-semibold text-sm uppercase tracking-wide text-muted-foreground">
                    Notes
                  </h3>
                  <div className="text-sm whitespace-pre-wrap">{job.notes}</div>
                </div>
              )}
              <div className="rounded-3xl border border-border bg-card p-5 shadow-soft">
                <h3 className="mb-3 font-semibold text-sm uppercase tracking-wide text-muted-foreground">
                  Print ticket
                </h3>
                <Link to="/app/staff/jobs/$id/ticket" params={{ id }}>
                  <Button className="w-full rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90">
                    <Printer className="h-4 w-4 mr-2" /> Print ticket
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="artwork" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            <Section title="Artwork uploads">
              <div
                {...getRootProps()}
                className={cn(
                  "mb-3 rounded-2xl border-2 border-dashed p-5 text-center text-sm transition-colors",
                  isDragActive ? "border-primary bg-primary/5" : "border-border bg-secondary/40",
                )}
              >
                <input {...getInputProps()} />
                <Upload className="mx-auto h-5 w-5 text-primary" />
                <p className="mt-2 text-xs text-muted-foreground">Drop files or click to upload</p>
              </div>
              {files.length === 0 ? (
                <p className="text-xs text-muted-foreground">No files yet.</p>
              ) : (
                <ul className="space-y-2">
                  {files.map((f) => (
                    <li key={f.id}>
                      <div className="flex items-center gap-2 rounded-2xl border border-border bg-secondary/30 px-3 py-2 text-sm hover:bg-secondary">
                        <a
                          href={f.file_url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-2 flex-1 min-w-0"
                        >
                          <FileImage className="h-4 w-4 text-primary shrink-0" />
                          <span className="truncate">{f.file_name}</span>
                        </a>
                        {f.file_type !== "gang_sheet" && (
                          <button
                            onClick={() => setAsGangSheet(f.id)}
                            className="text-xs text-primary hover:text-primary/80 shrink-0 whitespace-nowrap"
                          >
                            Set as gang sheet
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section title="Gang sheet">
              {(() => {
                const gangSheetFiles = files.filter((f) => f.file_type === "gang_sheet");
                if (gangSheetFiles.length === 0) {
                  return (
                    <div className="rounded-2xl bg-secondary/40 p-6 text-center">
                      <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-primary/10">
                        <Layers className="h-6 w-6 text-primary" />
                      </div>
                      <p className="mt-3 text-sm font-medium text-foreground">
                        No gang sheet attached yet
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        This is where the final production sheet will live.
                      </p>
                      <Button disabled className="mt-4 w-full rounded-full" variant="outline">
                        Attach gang sheet file
                      </Button>
                    </div>
                  );
                }
                return (
                  <ul className="space-y-2">
                    {gangSheetFiles.map((f) => (
                      <li key={f.id}>
                        <a
                          href={f.file_url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-2 rounded-2xl border border-primary/30 bg-primary/5 px-3 py-2 text-sm hover:bg-primary/10"
                        >
                          <FileImage className="h-4 w-4 text-primary" />
                          <span className="truncate font-medium">{f.file_name}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </Section>
          </div>
        </TabsContent>

        <TabsContent value="activity" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            <Section title="Activity timeline">
              {activity.length === 0 ? (
                <p className="text-xs text-muted-foreground">No activity yet.</p>
              ) : (
                <ol className="space-y-3">
                  {activity.map((a) => (
                    <li key={a.id} className="flex gap-3">
                      <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
                      <div className="text-sm">
                        <div>{a.message}</div>
                        <div className="text-xs text-muted-foreground">
                          {format(new Date(a.created_at), "d MMM HH:mm")}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </Section>

            <Section title="Add internal note">
              <Textarea
                rows={6}
                value={draftEdit.draft.notes_append}
                onChange={(e) => draftEdit.set("notes_append", e.target.value)}
                placeholder="Note for the team…"
                className="rounded-2xl"
              />
              <p className="mt-2 text-xs text-muted-foreground">
                Notes are queued with other edits and saved when you publish.
              </p>
            </Section>
          </div>
        </TabsContent>
      </Tabs>
      <DraftPublishBar
        changes={draftEdit.changes}
        errors={draftEdit.errors}
        isDirty={draftEdit.isDirty}
        canPublish={draftEdit.canPublish}
        publishing={draftEdit.publishing}
        onDiscard={() => draftEdit.reset(initialDraft)}
        onPublish={draftEdit.publish}
        entityLabel="job changes"
      />
      <AlertDialog
        open={pendingReopenDesignId !== null}
        onOpenChange={(open) => {
          if (!open && !versioningGangSheet) setPendingReopenDesignId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reopen gang sheet builder?</AlertDialogTitle>
            <AlertDialogDescription>
              This job already has a saved gang sheet
              {job.gangsheet_saved_at
                ? ` (last saved ${new Date(job.gangsheet_saved_at).toLocaleString(undefined, {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })})`
                : ""}
              . Saving changes in the builder will replace the current preview and timestamp. You
              can keep a snapshot of the existing version on this job before continuing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
            <AlertDialogCancel disabled={versioningGangSheet}>Cancel</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={confirmReopenWithVersion}
              disabled={versioningGangSheet}
            >
              {versioningGangSheet ? "Saving snapshot…" : "Save snapshot & reopen"}
            </Button>
            <AlertDialogAction onClick={confirmReopenOverwrite} disabled={versioningGangSheet}>
              Overwrite & reopen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-3xl border border-border bg-card p-5 shadow-soft">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h2>
      {children}
    </div>
  );
}
function Field({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{k}</dt>
      <dd className="font-medium">{v}</dd>
    </div>
  );
}
